/**
 * Client Dashboard (renamed from Weekly Management per CORE-D-01) —
 * operator KPI targets ("below plan" goals).
 *
 * Per-tenant goals the operator sets once (target CPA / CPC / conversion-rate /
 * impression-share). The weekly KPI monitor reads the CURRENT goal and flags an
 * account sitting BELOW PLAN — distinct from period-over-period drift.
 *
 * One editable row per supported metric; a blank/cleared value reverts that
 * metric to drift-only. Money metrics take a $ value, rate metrics a %. Sets a
 * tolerance band (default 10%) before a miss is flagged. Flags-only view:
 * this drives FLAGS only — never a Google Ads write. Operator-only.
 *
 * See ai-docs/google-ads/operator-kpi-targets-spec.md.
 */
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Loader2, Target } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  getKpiTargets,
  saveKpiTarget,
  type KpiTargetItem,
  type KpiTargetsState,
} from '@/services/googleAdsWeeklyService';

const TARGETS_KEY = (tenantId: string) => ['ga-weekly-kpi-targets', tenantId];

/** Convert a stored value to what the operator types (rates as %). */
function toInput(item: KpiTargetItem): string {
  if (item.target_value === null || item.target_value === undefined) return '';
  return item.fmt === 'rate'
    ? String(+(item.target_value * 100).toFixed(2))
    : String(item.target_value);
}

/** Convert the operator's typed value back to storage units (rates → fraction). */
function fromInput(fmt: string, raw: string): number | null {
  const t = raw.trim();
  if (t === '') return null;
  const n = Number(t);
  if (!Number.isFinite(n) || n <= 0) return null;
  return fmt === 'rate' ? n / 100 : n;
}

function unit(item: KpiTargetItem): string {
  return item.fmt === 'money' ? '$' : '%';
}

function goalHint(item: KpiTargetItem): string {
  const dir = item.lower_better ? 'at or below' : 'at or above';
  return `flag when ${dir} plan is missed`;
}

interface RowProps {
  item: KpiTargetItem;
  tenantId: string;
}

function TargetRow({ item, tenantId }: RowProps) {
  const qc = useQueryClient();
  const [value, setValue] = useState(toInput(item));
  const [tol, setTol] = useState(String(Math.round(item.tolerance_pct * 100)));

  // Re-sync when the server payload changes (e.g. after another row's save).
  useEffect(() => {
    setValue(toInput(item));
    setTol(String(Math.round(item.tolerance_pct * 100)));
  }, [item.target_value, item.tolerance_pct]);

  const save = useMutation({
    mutationFn: (target_value: number | null) =>
      saveKpiTarget(tenantId, {
        metric: item.metric,
        target_value,
        tolerance_pct: Math.max(0, Math.min(100, Number(tol) || 10)) / 100,
      }),
    onSuccess: (data: KpiTargetsState) => {
      qc.setQueryData(TARGETS_KEY(tenantId), data);
    },
  });

  const parsed = fromInput(item.fmt, value);
  const stored = item.target_value;
  const dirty =
    parsed !== stored ||
    Math.round(item.tolerance_pct * 100) !== (Number(tol) || 0);
  const hasGoal = stored !== null && stored !== undefined;

  return (
    <div className="flex flex-wrap items-end gap-3 rounded border border-border bg-background p-2.5">
      <div className="min-w-[130px]">
        <div className="text-sm font-medium">{item.label}</div>
        <div className="text-[11px] text-muted-foreground">{goalHint(item)}</div>
      </div>

      <label className="flex flex-col gap-1">
        <span className="text-[10px] uppercase text-muted-foreground">Goal</span>
        <div className="flex items-center gap-1">
          {item.fmt === 'money' && <span className="text-xs text-muted-foreground">$</span>}
          <Input
            className="h-8 w-24 tabular-nums"
            inputMode="decimal"
            placeholder="—"
            value={value}
            onChange={(e) => setValue(e.target.value)}
          />
          {item.fmt === 'rate' && <span className="text-xs text-muted-foreground">%</span>}
        </div>
      </label>

      <label className="flex flex-col gap-1">
        <span className="text-[10px] uppercase text-muted-foreground">Tolerance</span>
        <div className="flex items-center gap-1">
          <Input
            className="h-8 w-16 tabular-nums"
            inputMode="numeric"
            value={tol}
            onChange={(e) => setTol(e.target.value)}
          />
          <span className="text-xs text-muted-foreground">%</span>
        </div>
      </label>

      <div className="flex items-center gap-2">
        <Button
          size="sm"
          className="h-8"
          disabled={!dirty || save.isPending}
          onClick={() => save.mutate(parsed)}
        >
          {save.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Save'}
        </Button>
        {hasGoal && (
          <Button
            size="sm"
            variant="outline"
            className="h-8"
            disabled={save.isPending}
            onClick={() => {
              setValue('');
              save.mutate(null);
            }}
          >
            Clear
          </Button>
        )}
      </div>

      <div className="ml-auto text-right text-[11px] text-muted-foreground">
        {hasGoal ? (
          <>
            <div className="tabular-nums">
              in force: {unit(item) === '$' ? '$' : ''}
              {item.fmt === 'rate'
                ? `${(stored! * 100).toFixed(1)}%`
                : stored}
              {unit(item) === '%' && item.fmt !== 'rate' ? '%' : ''}
            </div>
            {item.effective_from && <div>since {item.effective_from}</div>}
            {item.set_by && <div>by {item.set_by}</div>}
          </>
        ) : (
          <span className="italic">no goal — drift only</span>
        )}
      </div>
    </div>
  );
}

export function KpiTargetsCard({ tenantId }: { tenantId: string }) {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: TARGETS_KEY(tenantId),
    queryFn: () => getKpiTargets(tenantId),
    enabled: !!tenantId,
  });

  const items = useMemo(() => data?.targets ?? [], [data]);
  const activeCount = items.filter(
    (i) => i.target_value !== null && i.target_value !== undefined,
  ).length;

  return (
    <div className="rounded-lg border border-border bg-warm-bg p-4">
      <div className="mb-1 flex flex-wrap items-center gap-2">
        <Target className="h-4 w-4 text-coral" />
        <span className="text-sm font-semibold">KPI goals (below-plan flagging)</span>
        <span className="text-[10px] uppercase text-muted-foreground">operator only</span>
        {activeCount > 0 && (
          <span className="rounded bg-coral-light/50 px-1.5 py-0.5 text-[10px] font-medium text-coral-dark">
            {activeCount} set
          </span>
        )}
      </div>
      <p className="mb-3 text-xs text-muted-foreground">
        Set per-client targets so the weekly KPI monitor flags an account that
        sits <strong>below plan</strong> — not just one moving the wrong way this
        period. A blank goal leaves that metric on drift-only. Goals drive flags,
        never Google Ads writes.
      </p>

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading goals…
        </div>
      ) : isError ? (
        <div className="flex items-center gap-2 text-sm text-destructive">
          <AlertCircle className="h-4 w-4" />
          {(error as Error)?.message || 'Failed to load KPI goals.'}
        </div>
      ) : items.length === 0 ? (
        <div className="text-sm text-muted-foreground">No supported metrics returned.</div>
      ) : (
        <div className="space-y-2">
          {items.map((item) => (
            <TargetRow key={item.metric} item={item} tenantId={tenantId} />
          ))}
        </div>
      )}
    </div>
  );
}

export default KpiTargetsCard;
